import { isDefined } from '@sama/js-core';
import { some } from '@sama/micro-dash';
import { ShapeState3D } from '../../cross-team-shared/annotation/shared/state/shape-state';
import { ShapeFilter } from '../../cross-team-shared/state/abstract-sensor-content-state';
import {
  IN_PROGRESS_INDEX,
  TRAINING_INDEX,
} from '../../team-apis/core/reserved-shape-indexes';
import {
  getSideViewRendererLayers,
  RendererLayer,
} from '../render/render-utils';
import { CuboidState3D, isCuboidState3D } from '../state/cuboid-state-3d';
import { PointCloudContentState } from '../state/point-cloud-content-state';
import { interpolateLocation } from './point-cloud-interpolation-helper';

export function hasMainRendererLayer(
  shape: ShapeState3D,
  state: PointCloudContentState,
) {
  return isDefined(determineMainRendererLayer(shape, state));
}

// eslint-disable-next-line complexity, max-lines-per-function -- prioritizing readability
export function determineRendererLayers(
  shape: ShapeState3D,
  state: PointCloudContentState,
  {
    areProjectionsEnabled,
  }: {
    areProjectionsEnabled: boolean;
  },
) {
  const { viewMode, projectionSettings } = state.render;
  if (isShapeInvisible(shape, state)) {
    return [];
  }

  if (shape.index === IN_PROGRESS_INDEX) {
    return [RendererLayer.Main, RendererLayer.Projection];
  }

  const shouldShowProjection =
    areProjectionsEnabled && projectionSettings.showShapes;
  if (shape.index === TRAINING_INDEX) {
    return determineTrainingShapeRendererLayers(shape, shouldShowProjection);
  }

  if (viewMode === 'none' || isShapeFiltered(shape, state)) {
    return [];
  }

  const rendererLayers = [
    determineMainRendererLayer(shape, state),
    determineProjectionRendererLayer(shape, state, shouldShowProjection),
  ];

  if (isCuboidState3D(shape)) {
    rendererLayers.push(
      ...determineSideViewLayers(shape, state),
      determineOrthographicRendererLayer(shape, state),
    );
  }

  return rendererLayers.filter(isDefined);
}

function determineTrainingShapeRendererLayers(
  shape: ShapeState3D,
  showShapeProjection: boolean,
) {
  const layers = [RendererLayer.Main];

  if (isCuboidState3D(shape)) {
    layers.push(RendererLayer.Orthographic, ...getSideViewRendererLayers());
  }

  if (showShapeProjection) {
    layers.push(RendererLayer.Projection);
  }

  return layers;
}

function isShapeFiltered(shape: ShapeState3D, state: PointCloudContentState) {
  const { shapeFilters, shapeTransientInfo } = state.render;
  return state.project.use_annotation_store
    ? // fix me if you change this code

      shapeTransientInfo[shape.index]?.isFilteredOut
    : isShapeFilteredObjectless(shape, shapeFilters);
}

function determineSideViewLayers(
  shape: ShapeState3D,
  state: PointCloudContentState,
) {
  const { selection } = state.render;
  if (
    isCuboidState3D(shape) &&
    shape.index in selection &&
    Object.keys(selection).length === 1
  ) {
    return getSideViewRendererLayers();
  }
  return [];
}

function determineProjectionRendererLayer(
  cuboid: ShapeState3D,
  state: PointCloudContentState,
  showShapeProjection: boolean,
) {
  const { selection, viewMode } = state.render;
  if (
    showShapeProjection &&
    (viewMode === 'all' ||
      (viewMode === 'selected' && cuboid.index in selection))
  ) {
    return RendererLayer.Projection;
  }

  return undefined;
}

function determineOrthographicRendererLayer(
  cuboid: CuboidState3D,
  state: PointCloudContentState,
) {
  const { selection, viewMode } = state.render;
  if (
    viewMode === 'all' ||
    (viewMode === 'selected' && cuboid.index in selection)
  ) {
    return RendererLayer.Orthographic;
  }
  return undefined;
}

function determineMainRendererLayer(
  shape: ShapeState3D,
  state: PointCloudContentState,
) {
  const { selection, viewMode } = state.render;
  if (isShapeInvisible(shape, state)) {
    return undefined;
  }

  if (
    viewMode === 'all' ||
    (viewMode === 'selected' && shape.index in selection)
  ) {
    return RendererLayer.Main;
  }
  return undefined;
}

function isShapeInvisible(shape: ShapeState3D, state: PointCloudContentState) {
  const { frameNumber } = state.render;
  const { visibility } = interpolateLocation(shape.key_locations, frameNumber);

  return visibility === 0;
}

/** @deprecated used for old projects without objects */
function isShapeFilteredObjectless(
  cuboid: ShapeState3D,
  shapeFilters: ShapeFilter[],
) {
  return some(
    shapeFilters,
    (shapeFilter) =>
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      !!shapeFilter?.filterNames.some(
        (tag) => tag === (cuboid.tags[shapeFilter.outputName] || 'none'),
      ),
  );
}
